import { useEffect, useState } from "react";
import { api } from "../lib/api";
import SuggestInput from "../components/SuggestInput";
import { useAuthStore } from "../store/authStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import RowActionsMenu, { IconTrash } from "../components/RowActionsMenu";
import { ConfirmDialog } from "../components/ui";
import { useT, useLang } from "../i18n";
import { useFmt, fmtDate as fmtDateI18n } from "../i18n/fmt";
import type { Lang } from "../i18n/langStore";

/** Sección Informes (F2): plantillas guardadas por organización + builder en
 *  modal. El builder también genera informes ad-hoc (vista previa / descarga)
 *  sin necesidad de guardar. La programación automática llega en F3. */

type BlockMeta = {
  key: string;
  name: { es: string; en: string };
  description: { es: string; en: string };
  has_title: boolean;
  min_plan?: string;
  params_schema: Record<string, unknown>;
};
type PF = { id: string; cn_code: string; product_name: string };

type Period = "month" | "last_month" | "last_quarter" | "custom";
type Scope = "portfolio_and_competitors" | "portfolio" | "competitors" | "market" | "product";

type Template = {
  id: string;
  name: string;
  language: "es" | "en";
  config: {
    period: Period;
    period_start?: string | null;
    period_end?: string | null;
    scope: Scope;
    product_cns?: string[];
    filters?: Record<string, string>;      // R-33/R-34: hito_group, hito_id, severity, channel, atc, ah, spr, pa, nregistro, cn
    sections: { type: string; params?: Record<string, unknown> }[];
  };
  schedule: "none" | "monthly" | "quarterly";
  schedule_day: number;
  recipients: string[];
  last_generated_at: string | null;
};

const PERIODS: { value: Period; labelKey: string }[] = [
  { value: "month", labelKey: "reports.periodMonth" },
  { value: "last_month", labelKey: "reports.periodLastMonth" },
  { value: "last_quarter", labelKey: "reports.periodLastQuarter" },
  { value: "custom", labelKey: "reports.periodCustom" },
];

const SCOPES: { value: Scope; labelKey: string }[] = [
  { value: "portfolio_and_competitors", labelKey: "reports.scopePortfolioAndCompetitors" },
  { value: "portfolio", labelKey: "reports.scopePortfolio" },
  { value: "competitors", labelKey: "reports.scopeCompetitors" },
  { value: "market", labelKey: "reports.scopeMarket" },
  { value: "product", labelKey: "reports.scopeProduct" },
];

const inp = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)", borderRadius: 12,
  color: "var(--clr-text)", padding: "0.5rem 0.85rem", fontSize: "0.82rem", outline: "none",
  width: "100%", boxSizing: "border-box" as const, ...extra,
});

const label: React.CSSProperties = {
  fontSize: "0.72rem", color: "var(--clr-text3)", display: "block", marginBottom: 6,
};

// Cápsula de TIPO (sin punto — regla v3: el punto es exclusivo de estados)
function Capsule({ text, color }: { text: string; color: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", background: color + "1e", color,
      borderRadius: 999, padding: "3px 11px", fontSize: "0.72rem", fontWeight: 600,
      whiteSpace: "nowrap",
    }}>{text}</span>
  );
}

function Pill({ active, text, onClick }: { active: boolean; text: string; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      background: active ? "var(--clr-org-primary)" : "var(--clr-surface)",
      color: active ? "#fff" : "var(--clr-text3)",
      border: active ? "1px solid transparent" : "1px solid var(--clr-border)",
      borderRadius: 999, padding: "0.42rem 1rem", fontSize: "0.78rem", fontWeight: 600,
      cursor: "pointer", whiteSpace: "nowrap",
    }}>{text}</button>
  );
}

function MiniBtn({ text, title, onClick, disabled, danger }: {
  text: string; title: string; onClick: () => void; disabled?: boolean; danger?: boolean;
}) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{
      background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 8,
      color: disabled ? "var(--clr-text4)" : danger ? "#ef4444" : "var(--clr-text2)",
      width: 26, height: 26, fontSize: "0.78rem", cursor: disabled ? "default" : "pointer",
      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>{text}</button>
  );
}

// Icono documento (Heroicons outline) para el kebab "Generar ahora"
const IconPdf = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12H9m5.25 3H9M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);
const IconEdit = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.862 4.487zm0 0L19.5 7.125" />
  </svg>
);

async function fetchPdf(path: string, body?: unknown): Promise<{ blob: Blob; filename: string }> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error((e as any).detail ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  return { blob, filename: m ? m[1] : "helixscan_informe.pdf" };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const fmtDate = (iso: string | null, lang: Lang) =>
  iso ? fmtDateI18n(iso, lang, { day: "2-digit", month: "2-digit", year: "numeric" }) : null;

export default function ReportsPage() {
  const t = useT();
  const fmt = useFmt();
  const { isMobile } = useBreakpoint();
  const role = useAuthStore(s => s.user?.role);
  const canWrite = role === "admin" || role === "analyst" || role === "superadmin";

  const [blocks, setBlocks] = useState<BlockMeta[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [builder, setBuilder] = useState<null | { editing: Template | null }>(null);
  const [pendingDelete, setPendingDelete] = useState<Template | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState("");

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(""), 5000); };

  const loadTemplates = () =>
    api.get<{ items: Template[] }>("/reports/templates").then(r => setTemplates(r.items));

  useEffect(() => {
    api.get<{ blocks: BlockMeta[] }>("/reports/blocks").then(r => setBlocks(r.blocks))
      .catch(e => flash("Error: " + e.message));
    loadTemplates().catch(e => flash("Error: " + e.message));
  }, []);

  const byKey = Object.fromEntries(blocks.map(b => [b.key, b]));

  const generateFromTemplate = async (tpl: Template) => {
    setBusyId(tpl.id);
    try {
      const { blob, filename } = await fetchPdf(`/reports/templates/${tpl.id}/generate`);
      downloadBlob(blob, filename);
      loadTemplates();
    } catch (e: any) { flash("Error: " + e.message); }
    finally { setBusyId(null); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try { await api.delete(`/reports/templates/${id}`); loadTemplates(); flash(t("reports.templateDeleted")); }
    catch (e: any) { flash("Error: " + e.message); }
  };

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>
          {t("reports.title")} {templates.length > 0 && <span style={{ color: "var(--clr-text4)", fontWeight: 400, fontSize: "1.05rem" }}>({templates.length})</span>}
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {msg && <span style={{ color: msg.startsWith("Error") ? "#f87171" : "var(--clr-text3)", fontSize: "0.82rem" }}>{msg}</span>}
          {canWrite && (
            <button onClick={() => setBuilder({ editing: null })} style={{
              background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999,
              padding: "0.5rem 1.3rem", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
            }}>{t("reports.newTemplate")}</button>
          )}
        </div>
      </div>
      <p style={{ margin: "0 0 18px", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
        {t("reports.intro")}
      </p>

      <OperationReportCard />

      {/* ── Lista de plantillas ── */}
      {templates.length === 0 ? (
        <div style={{
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18,
          backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
          boxShadow: "var(--clr-shadow-sm)", padding: "2.5rem", textAlign: "center",
        }}>
          <div style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: 6 }}>{t("reports.noTemplates")}</div>
          <div style={{ fontSize: "0.82rem", color: "var(--clr-text3)", marginBottom: canWrite ? 18 : 0 }}>
            {canWrite
              ? t("reports.noTemplatesWrite")
              : t("reports.noTemplatesRead")}
          </div>
          {canWrite && (
            <button onClick={() => setBuilder({ editing: null })} style={{
              background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999,
              padding: "0.5rem 1.3rem", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
            }}>{t("reports.createTemplate")}</button>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
          {templates.map(tpl => (
            <div key={tpl.id} style={{
              background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18,
              backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
              boxShadow: "var(--clr-shadow-sm)", padding: "1.1rem 1.25rem",
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.95rem", fontWeight: 700, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {tpl.name}
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "var(--clr-text3)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {tpl.config.sections.map(s => byKey[s.type]?.name[fmt.lang] ?? s.type).join(" · ")}
                  </div>
                </div>
                {/* Generar sin pasar por el menú (feedback de Carlos, 20 oct 2026) */}
                <button onClick={() => { if (busyId !== tpl.id) generateFromTemplate(tpl); }} disabled={busyId === tpl.id}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--clr-org-primary)", color: "#fff",
                           border: 0, borderRadius: 999, padding: "0.4rem 0.95rem", fontSize: "0.76rem", fontWeight: 600,
                           cursor: busyId === tpl.id ? "default" : "pointer", opacity: busyId === tpl.id ? 0.6 : 1, whiteSpace: "nowrap" }}>
                  {IconPdf}{busyId === tpl.id ? t("reports.generating") : t("reports.generate")}
                </button>
                {canWrite && (
                  <RowActionsMenu actions={[
                    { label: t("reports.edit"), icon: IconEdit, onClick: () => setBuilder({ editing: tpl }) },
                    { label: t("reports.delete"), icon: <IconTrash />, onClick: () => setPendingDelete(tpl), danger: true },
                  ]} />
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Capsule text={tpl.language.toUpperCase()} color="#0891b2" />
                <Capsule text={(() => { const p = PERIODS.find(p => p.value === tpl.config.period); return p ? t(p.labelKey) : tpl.config.period; })()} color="#f59e0b" />
              </div>
              <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                {tpl.last_generated_at ? t("reports.lastReport", { date: fmtDate(tpl.last_generated_at, fmt.lang) ?? "" }) : t("reports.neverGenerated")}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t("reports.deleteTitle")}
        message={pendingDelete ? t("reports.deleteMessage", { name: pendingDelete.name }) : undefined}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {builder && (
        <BuilderModal
          blocks={blocks}
          editing={builder.editing}
          canWrite={canWrite}
          onClose={() => setBuilder(null)}
          onSaved={() => { setBuilder(null); loadTemplates(); flash(t("reports.templateSaved")); }}
          onError={flash}
        />
      )}
    </div>
  );
}

/* ── Builder (modal v3) ─────────────────────────────────────────────────────── */

function BuilderModal({ blocks, editing, canWrite, onClose, onSaved, onError }: {
  blocks: BlockMeta[];
  editing: Template | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const t = useT();
  const lang = useLang();
  const { isMobile } = useBreakpoint();
  const plan = useAuthStore(s => s.user?.plan);
  const role = useAuthStore(s => s.user?.role);
  const blockLocked = (b: BlockMeta) =>
    b.min_plan === "ultra" && plan !== "ultra" && role !== "superadmin";
  const [name, setName] = useState(editing?.name ?? "");
  const [language, setLanguage] = useState<"es" | "en">(editing?.language ?? "es");
  const [period, setPeriod] = useState<Period>(editing?.config.period ?? "last_month");
  const [customStart, setCustomStart] = useState(editing?.config.period_start ?? "");
  const [customEnd, setCustomEnd] = useState(editing?.config.period_end ?? "");
  const [scope, setScope] = useState<Scope>(editing?.config.scope ?? "portfolio_and_competitors");
  const [products, setProducts] = useState<PF[]>([]);
  const [productCns, setProductCns] = useState<string[]>(editing?.config.product_cns ?? []);
  // Filtros por taxonomía de hitos y jerarquía de productos (R-33/R-34, §90)
  const [filters, setFilters] = useState<Record<string, string>>(editing?.config.filters ?? {});
  const [hitoGroups, setHitoGroups] = useState<{ grupo: number }[]>([]);
  const [hierKind, setHierKind] = useState<string>(() => {
    const f = editing?.config.filters ?? {};
    return ["atc", "ah", "spr", "pa", "nregistro", "cn"].find(k => f[k]) ?? "atc";
  });
  useEffect(() => { api.get<{ groups: { grupo: number }[] }>("/hitos").then(r => setHitoGroups(r.groups)).catch(() => {}); }, []);
  const setFilter = (k: string, v: string) => setFilters(f => { const n = { ...f }; if (v) n[k] = v; else delete n[k]; return n; });
  const setHierValue = (v: string) => setFilters(f => {
    const n = { ...f }; for (const k of ["atc", "ah", "spr", "pa", "nregistro", "cn"]) delete n[k];
    if (v) n[hierKind] = v; return n;
  });
  const [sections, setSections] = useState<string[]>(
    editing?.config.sections.map(s => s.type)
    ?? ["kpis", "hito_kpis", "portfolio_changes", "competitor_changes", "radar_summary"].filter(k => blocks.some(b => b.key === k))
  );
  const [busy, setBusy] = useState<null | "preview" | "download" | "save">(null);

  useEffect(() => {
    if (scope === "product" && products.length === 0) {
      api.get<{ items: PF[] }>("/portfolio?limit=200").then(r => setProducts(r.items)).catch(() => {});
    }
  }, [scope]);

  const byKey = Object.fromEntries(blocks.map(b => [b.key, b]));
  const available = blocks.filter(b => !sections.includes(b.key));

  const move = (i: number, delta: number) => {
    setSections(s => {
      const next = [...s];
      const j = i + delta;
      if (j < 0 || j >= next.length) return s;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const configReady =
    sections.length > 0 &&
    (period !== "custom" || Boolean(customStart && customEnd)) &&
    (scope !== "product" || productCns.length > 0);

  const buildConfig = () => {
    const config: Record<string, unknown> = {
      period, scope,
      product_cns: scope === "product" ? productCns : [],
      filters,
      sections: sections.map(type => ({ type })),
    };
    if (period === "custom") { config.period_start = customStart; config.period_end = customEnd; }
    return config;
  };

  const generate = async (mode: "preview" | "download") => {
    setBusy(mode);
    try {
      const { blob, filename } = await fetchPdf("/reports/generate",
        { title: name.trim() || null, language, config: buildConfig() });
      if (mode === "preview") window.open(URL.createObjectURL(blob), "_blank");
      else downloadBlob(blob, filename);
    } catch (e: any) { onError("Error: " + e.message); }
    finally { setBusy(null); }
  };

  const save = async () => {
    setBusy("save");
    try {
      // Envío por email DORMIDO (decisión 7-jul-2026: email solo para alertas);
      // los informes se generan y descargan desde la plataforma.
      const payload = {
        name: name.trim(),
        language,
        config: buildConfig(),
        schedule: "none",
        schedule_day: 1,
        recipients: [],
      };
      if (editing) await api.put(`/reports/templates/${editing.id}`, payload);
      else await api.post("/reports/templates", payload);
      onSaved();
    } catch (e: any) { onError("Error: " + e.message); }
    finally { setBusy(null); }
  };

  const secondaryBtn = (enabled: boolean): React.CSSProperties => ({
    background: "var(--clr-modal-solid2)", color: enabled ? "var(--clr-text2)" : "var(--clr-text4)",
    border: "1px solid var(--clr-border2)", borderRadius: 999, padding: "0.5rem 1.3rem",
    fontSize: "0.8rem", fontWeight: 600, cursor: enabled ? "pointer" : "default",
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,18,21,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", padding: 16 }}>
      <div style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "1.75rem", width: "min(780px, 100%)", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)" }}>
        <h3 style={{ margin: "0 0 1.25rem", color: "var(--clr-text)", fontSize: "1.05rem", fontWeight: 700, letterSpacing: "-0.01em" }}>
          {editing ? t("reports.editTemplate") : t("reports.newReportTemplate")}
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 14 : 22 }}>
          {/* Columna izquierda: configuración */}
          <div>
            <div style={{ marginBottom: 14 }}>
              <span style={label}>{t("reports.templateName")} {canWrite ? "*" : t("reports.optionalToGenerate")}</span>
              <input value={name} onChange={e => setName(e.target.value)} maxLength={120}
                     placeholder={t("reports.templateNamePlaceholder")} style={inp()} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <span style={label}>{t("reports.pdfLanguage")}</span>
              <div style={{ display: "flex", gap: 8 }}>
                <Pill active={language === "es"} text={t("reports.spanish")} onClick={() => setLanguage("es")} />
                <Pill active={language === "en"} text={t("reports.english")} onClick={() => setLanguage("en")} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <span style={label}>{t("reports.period")}</span>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {PERIODS.map(p => (
                  <Pill key={p.value} active={period === p.value} text={t(p.labelKey)} onClick={() => setPeriod(p.value)} />
                ))}
              </div>
              {period === "custom" && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <label style={{ flex: 1 }}>
                    <span style={label}>{t("reports.from")}</span>
                    <input type="date" value={customStart ?? ""} onChange={e => setCustomStart(e.target.value)} style={inp()} />
                  </label>
                  <label style={{ flex: 1 }}>
                    <span style={label}>{t("reports.to")}</span>
                    <input type="date" value={customEnd ?? ""} onChange={e => setCustomEnd(e.target.value)} style={inp()} />
                  </label>
                </div>
              )}
            </div>

            <div>
              <span style={label}>{t("reports.scope")}</span>
              <select value={scope} onChange={e => setScope(e.target.value as Scope)} style={inp()}>
                {SCOPES.map(s => <option key={s.value} value={s.value}>{t(s.labelKey)}</option>)}
              </select>
              {scope === "product" && (
                <div style={{ marginTop: 10, maxHeight: 160, overflowY: "auto", border: "1px solid var(--clr-border2)", borderRadius: 12, padding: "0.5rem 0.75rem" }}>
                  {products.length === 0 && (
                    <span style={{ fontSize: "0.78rem", color: "var(--clr-text4)" }}>{t("reports.noProducts")}</span>
                  )}
                  {products.map(p => (
                    <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: "0.8rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={productCns.includes(p.cn_code)}
                        onChange={e => setProductCns(cns => e.target.checked
                          ? [...cns, p.cn_code]
                          : cns.filter(c => c !== p.cn_code))}
                      />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.product_name}</span>
                      <span style={{ color: "var(--clr-text4)", fontSize: "0.72rem", marginLeft: "auto" }}>{p.cn_code}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Filtros por hito y jerarquía (R-33/R-34, §90) */}
            <div style={{ marginTop: 14 }}>
              <span style={label}>{t("reports.filtersTitle")}</span>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <select value={filters.hito_group ?? ""} onChange={e => setFilter("hito_group", e.target.value)} style={inp()}>
                  <option value="">{t("hitos.allGroups")}</option>
                  {hitoGroups.map(g => <option key={g.grupo} value={g.grupo}>{t(`hitos.group${g.grupo}`)}</option>)}
                </select>
                <select value={filters.severity ?? ""} onChange={e => setFilter("severity", e.target.value)} style={inp()}>
                  <option value="">{t("hitos.allSeverities")}</option>
                  <option value="A">{t("hitos.sevA")}</option>
                  <option value="D">{t("hitos.sevD")}</option>
                  <option value="I">{t("hitos.sevI")}</option>
                </select>
                <select value={filters.channel ?? ""} onChange={e => setFilter("channel", e.target.value)} style={inp()}>
                  <option value="">{t("reports.channelAll")}</option>
                  <option value="Farmacia">{t("reports.channelPharmacy")}</option>
                  <option value="Hospital">{t("reports.channelHospital")}</option>
                </select>
                <select value={hierKind} onChange={e => { setHierKind(e.target.value); setHierValue(""); }} style={inp()}>
                  {["atc", "ah", "spr", "pa", "nregistro", "cn"].map(k => <option key={k} value={k}>{t(`reports.hier_${k}`)}</option>)}
                </select>
              </div>
              <div style={{ marginTop: 8 }}>
                <SuggestInput kind={hierKind} value={filters[hierKind] ?? ""} onChange={setHierValue}
                  placeholder={t(`reports.hierPlaceholder_${hierKind}`)} style={{ ...inp(), width: "100%" }} />
              </div>
              {Object.keys(filters).length > 0 && (
                <button onClick={() => setFilters({})} style={{ marginTop: 6, background: "transparent", border: 0, color: "var(--clr-text4)", cursor: "pointer", fontSize: "0.75rem", padding: 0 }}>
                  {t("reports.filtersClear")}
                </button>
              )}
            </div>
          </div>

          {/* Columna derecha: secciones */}
          <div>
            <span style={label}>{t("reports.reportSections")} {sections.length > 0 && `(${sections.length})`}</span>
            {sections.length === 0 && (
              <p style={{ fontSize: "0.78rem", color: "var(--clr-text4)", margin: "0 0 10px" }}>
                {t("reports.addAtLeastOne")}
              </p>
            )}
            {sections.map((key, i) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "0.5rem 0.65rem", border: "1px solid var(--clr-border2)", borderRadius: 12, marginBottom: 8 }}>
                <span style={{ color: "var(--clr-text4)", fontSize: "0.72rem", width: 12, textAlign: "right", flexShrink: 0 }}>{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>{byKey[key]?.name[lang] ?? key}</div>
                  {byKey[key]?.description[lang] && (
                    <div style={{ fontSize: "0.7rem", color: "var(--clr-text3)", lineHeight: 1.35, marginTop: 2 }}>
                      {byKey[key].description[lang]}
                    </div>
                  )}
                </div>
                <MiniBtn text="↑" title={t("reports.moveUp")} onClick={() => move(i, -1)} disabled={i === 0} />
                <MiniBtn text="↓" title={t("reports.moveDown")} onClick={() => move(i, +1)} disabled={i === sections.length - 1} />
                <MiniBtn text="✕" title={t("reports.removeSection")} onClick={() => setSections(s => s.filter(k => k !== key))} danger />
              </div>
            ))}

            {available.length > 0 && (
              <>
                <div style={{ fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", margin: "12px 0 8px" }}>
                  {t("reports.addSection")}
                </div>
                {available.map(b => {
                  const locked = blockLocked(b);
                  return (
                    <button key={b.key} disabled={locked}
                            onClick={() => !locked && setSections(s => [...s, b.key])}
                            title={locked ? t("reports.availableInUltra") : b.description[lang]} style={{
                      display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                      background: "transparent", border: "1px dashed var(--clr-border)", borderRadius: 12,
                      padding: "0.5rem 0.65rem", marginBottom: 8, cursor: locked ? "default" : "pointer",
                      color: locked ? "var(--clr-text4)" : "var(--clr-text2)",
                      fontFamily: "inherit", fontSize: "0.82rem", opacity: locked ? 0.7 : 1,
                    }}>
                      <span style={{ color: locked ? "var(--clr-text4)" : "var(--clr-org-primary)", fontWeight: 700, flexShrink: 0 }}>+</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{b.name[lang]}</div>
                        <div style={{ fontSize: "0.7rem", color: "var(--clr-text3)", lineHeight: 1.35, marginTop: 2 }}>{b.description[lang]}</div>
                      </div>
                      {b.min_plan === "ultra" && <Capsule text="ULTRA" color="#a78bfa" />}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20, flexWrap: "wrap" }}>
          <button onClick={onClose} style={secondaryBtn(true)}>{t("reports.cancel")}</button>
          <button onClick={() => generate("preview")} disabled={!!busy || !configReady} style={secondaryBtn(!busy && configReady)}>
            {busy === "preview" ? t("reports.generating") : t("reports.preview")}
          </button>
          <button onClick={() => generate("download")} disabled={!!busy || !configReady} style={secondaryBtn(!busy && configReady)}>
            {busy === "download" ? t("reports.generating") : t("reports.downloadPdf")}
          </button>
          {canWrite && (
            <button onClick={save} disabled={!!busy || !configReady || !name.trim()} style={{
              background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999,
              padding: "0.5rem 1.3rem", fontSize: "0.8rem", fontWeight: 600,
              cursor: !!busy || !configReady || !name.trim() ? "default" : "pointer",
              opacity: !!busy || !configReady || !name.trim() ? 0.6 : 1,
            }}>{busy === "save" ? t("reports.saving") : editing ? t("reports.saveChanges") : t("reports.saveTemplate")}</button>
          )}
        </div>
      </div>
    </div>
  );
}


/** Informe de Operación Inicial (contrato con Italfarmaco, punto 16): ejecuciones por fuente,
 *  eventos y avisos, disponibilidad e incidencias de al menos treinta días de producción. */
function OperationReportCard() {
  const t = useT();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const ayer = new Date(Date.now() - 86400000);
  const [hasta, setHasta] = useState(iso(ayer));
  const [desde, setDesde] = useState(iso(new Date(ayer.getTime() - 29 * 86400000)));
  // Con puesta en producción registrada, el periodo por defecto son sus treinta días (Carlos, 23 oct 2026).
  const [goLive, setGoLive] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ go_live_at: string | null; desde: string; hasta: string }>("/reports/operacion/periodo")
      .then(p => { setGoLive(p.go_live_at); setDesde(p.desde); setHasta(p.hasta); }).catch(() => {});
  }, []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const dias = Math.round((new Date(hasta).getTime() - new Date(desde).getTime()) / 86400000) + 1;
  const go = async () => {
    setBusy(true); setErr("");
    try { await api.download(`/reports/operacion?desde=${desde}&hasta=${hasta}`, "helixscan_informe_operacion.pdf"); }
    catch (e: unknown) { setErr((e as Error)?.message ?? "Error"); }
    finally { setBusy(false); }
  };
  const inp: React.CSSProperties = { background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)", borderRadius: 10,
    color: "var(--clr-text)", padding: "5px 10px", fontSize: "0.8rem" };
  return (
    <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1rem 1.25rem",
                  marginBottom: 16, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontWeight: 700, fontSize: "0.92rem" }}>{t("reports.opTitle")}</div>
        <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)", marginTop: 3, lineHeight: 1.5 }}>{t("reports.opIntro")}</div>
        <div style={{ fontSize: "0.75rem", color: "var(--clr-text3)", marginTop: 4 }}>
          {goLive ? t("reports.opGoLive", { date: new Date(goLive).toLocaleString("es-ES", { timeZone: "Europe/Madrid",
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) }) : t("reports.opNoGoLive")}
        </div>
        {dias < 30 && <div style={{ fontSize: "0.75rem", color: "#b45309", marginTop: 4 }}>{t("reports.opShort", { n: dias })}</div>}
        {err && <div role="alert" style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: 4 }}>{err}</div>}
      </div>
      <label style={{ fontSize: "0.75rem", color: "var(--clr-text3)", display: "inline-flex", gap: 6, alignItems: "center" }}>
        {t("reports.opFrom")} <input type="date" value={desde} max={hasta} onChange={e => setDesde(e.target.value)} style={inp} />
      </label>
      <label style={{ fontSize: "0.75rem", color: "var(--clr-text3)", display: "inline-flex", gap: 6, alignItems: "center" }}>
        {t("reports.opTo")} <input type="date" value={hasta} min={desde} onChange={e => setHasta(e.target.value)} style={inp} />
      </label>
      <button onClick={go} disabled={busy || !desde || !hasta}
        style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "8px 18px",
                 fontSize: "0.8rem", fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? t("export.busy") : t("reports.opGenerate")}
      </button>
    </div>
  );
}
